import * as React from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Avatar from '@mui/material/Avatar';
import CardHeader from '@mui/material/CardHeader';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import UserTableRowViewModel from './ViewModels/UserTableRowViewModel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { APIContext } from '../../Contexts/APIContext';

interface IProps {
    row: UserTableRowViewModel;
    refresh: () => void;
    edit: (id: string) => void;
}

export default function UserManagementRow(props: IProps) {
    const { row, refresh, edit } = props;
    const { get, showMessage } = React.useContext(APIContext);
    const [loading, setLoading] = React.useState(false);
    const [anchorRef, setAnchorRef] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorRef);

    const reset = async (event: any) => {
        event.stopPropagation();
        const action = `GenerateResetToken?email=${encodeURIComponent(row.email)}`;
        setLoading(true);
        await get('User', action, (success: any) => {
            showMessage('success', 'If a user exists an email will be sent with the reset link');
        })
        setLoading(false);
    }

    const confirm = async (event: any) => {
        event.stopPropagation();
        setLoading(true);
        await get("User", `ResendEmailConfirmation?id=${row.id}`, (success: any) => {
            showMessage('success', 'Password reset email sent');
        });
        setLoading(false);
    }

    const toggleArchive = async (event: any) => {
        event.stopPropagation();
        setLoading(true);
        await get("User", `UpdateArchiveStatus?id=${row.id}`, (success: any) => {
            showMessage('success', 'User has been ' + (row.archived ? "Activated" : "Archived"));
        });
        setLoading(false);
        refresh();
    }

    const handleClose = (event: any) => {
        event.stopPropagation();
        setAnchorRef(null);
    };

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorRef(event.currentTarget);
    };

    return (
        <TableRow>
            <TableCell>
                <CardHeader
                    avatar={
                        <Avatar {...stringAvatar(row.firstName + ' ' + row.lastName)} />
                    }
                    title={row.firstName + ' ' + row.lastName}
                />
            </TableCell>
            <TableCell>{row.emailConfirmed && <CheckCircleIcon color="success" fontSize="small" sx={{ m: '0px 4px -5px 0px' }} />}{row.email}</TableCell>
            <TableCell align='right'>
                <Grid container spacing={1} justifyContent='flex-end' alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                    <Grid>
                        <IconButton size="small" disabled={loading} onClick={handleToggle} aria-label="Staff actions dropdown">
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            open={open}
                            onClose={handleClose}
                            anchorEl={anchorRef}
                        >
                            <MenuItem
                                onClick={() => edit(row.id)}
                                disabled={loading}
                            >
                                Edit
                            </MenuItem>
                            <MenuItem
                                onClick={toggleArchive}
                                disabled={loading}
                            >
                                {row.archived ? "Activate" : "Archive"}
                            </MenuItem>
                            {row.emailConfirmed &&
                                <MenuItem
                                    onClick={reset}
                                    aria-label='reset password'
                                    disabled={loading}
                                >
                                    Reset Password
                                </MenuItem>
                            }
                            {!row.emailConfirmed &&
                                <MenuItem
                                    onClick={confirm}
                                    aria-label='Send confirmation email'
                                    disabled={loading}
                                >
                                    Re-send Confirmation
                                </MenuItem>
                            }
                        </Menu>
                    </Grid>
                </Grid>
            </TableCell>
        </TableRow>
    );
}

function stringAvatar(name: string) {
    if (name.trim().length <= 1) {
        name = "N A"
    }
    var words = name.split(' ').filter((item: string) => item !== '');
    var initals = words[0][0].toUpperCase() + words[1][0].toUpperCase();

    return {
        sx: {
            bgcolor: '#BDBDBD',
        },
        children: initals,
    };
}